import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { LyraAvatar } from '@/components/LyraAvatar';
import { usePersonalizationData } from '@/hooks/usePersonalizationData';
import { createPath } from '@/services/userPathService';
import {
  getLicenseFromStorage,
  activateLicense,
  clearLicenseFromStorage,
  extractFirstName,
  LicenseValidationResult
} from '@/services/licenseService';

export const Auth = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [loading, setLoading] = useState(false);
  const [isFromOnboarding, setIsFromOnboarding] = useState(false);
  const [activeTab, setActiveTab] = useState('signup');
  const [licenseData, setLicenseData] = useState<LicenseValidationResult | null>(null);
  const [isPrefilledFromLicense, setIsPrefilledFromLicense] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { savePersonalizationData } = usePersonalizationData();

  useEffect(() => {
    // Check if user is coming from personalization or onboarding flow
    const pendingPersonalization = localStorage.getItem('pendingPersonalization');
    const pendingOnboarding = localStorage.getItem('lyra-onboarding-temp');
    const params = new URLSearchParams(location.search);
    const wantsSignin = params.get('signin') === 'true';
    const wantsSignup = params.get('signup') === 'true';
    const fromOnboarding = Boolean(pendingPersonalization || pendingOnboarding);

    setIsFromOnboarding(fromOnboarding);

    // Check for license data and pre-fill form
    const storedLicense = getLicenseFromStorage();
    if (storedLicense?.valid) {
      setLicenseData(storedLicense);
      // Pre-fill name and email from purchase data
      if (storedLicense.customerName) {
        setFirstName(extractFirstName(storedLicense.customerName));
      }
      if (storedLicense.customerEmail) {
        setEmail(storedLicense.customerEmail);
      }
      setIsPrefilledFromLicense(true);
    }

    if (wantsSignin) {
      setActiveTab('signin');
    } else if (wantsSignup || fromOnboarding) {
      setActiveTab('signup');
    }
  }, [location.search]);

  const applyPendingOnboardingPath = async (userId?: string) => {
    if (!userId) return;
    const pendingOnboarding = localStorage.getItem('lyra-onboarding-temp');
    if (!pendingOnboarding) return;

    try {
      const selections = JSON.parse(pendingOnboarding);
      await createPath(selections.focus, selections.challenge, {
        userId,
        style: selections.style
      });
      localStorage.removeItem('lyra-onboarding-temp');
    } catch (err) {
      console.error('Error saving onboarding path after auth:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent, isLogin: boolean) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password
        });
        if (error) throw error;
        await applyPendingOnboardingPath(data.user?.id);

        // Redirect to the original destination if available, otherwise dashboard
        const from = (location.state as any)?.from?.pathname || '/micro-dashboard';
        navigate(from);
      } else {
        const { data, error } = await supabase.auth.signUp({
          email,
          password
        });
        if (error) throw error;

        // Create minimal profile with first_name and email
        if (data.user) {
          try {
            await supabase.from('profiles').upsert({
              user_id: data.user.id,
              email: data.user.email,
              first_name: firstName,
              profile_completed: false // Profile is NOT completed, but user can access content
            });
          } catch (err) {
            console.error('Error creating profile:', err);
          }
        }

        // Handle pending personalization data after successful signup
        const pendingPersonalization = localStorage.getItem('pendingPersonalization');
        if (pendingPersonalization && data.user) {
          try {
            const personalizationData = JSON.parse(pendingPersonalization);
            await savePersonalizationData(personalizationData, data.user.id);
            localStorage.removeItem('pendingPersonalization');
          } catch (err) {
            console.error('Error saving personalization data:', err);
          }
        }

        // Apply onboarding selections captured before signup
        await applyPendingOnboardingPath(data.user?.id);

        // Activate license if one was used for signup
        if (licenseData?.licenseKey && data.user) {
          try {
            const activationResult = await activateLicense(licenseData.licenseKey);
            if (activationResult.success) {
              console.log('License activated successfully');
            } else {
              console.error('License activation failed:', activationResult.error);
              // Don't block signup, but warn user
              toast({
                title: "Note",
                description: "Your account was created, but we couldn't link your license. Please contact support.",
                variant: "default"
              });
            }
            // Clear license from storage regardless of activation result
            clearLicenseFromStorage();
          } catch (err) {
            console.error('Error activating license:', err);
            clearLicenseFromStorage();
          }
        }

        toast({
          title: "Account created!",
          description: "You've been signed up and logged in successfully."
        });
        navigate('/micro-dashboard');
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full border-0 shadow-xl bg-white/80 backdrop-blur-sm">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <LyraAvatar />
          </div>
          <CardTitle className="text-2xl font-bold">
            {isFromOnboarding ? "Almost There!" : "Welcome to Your AI Journey"}
          </CardTitle>
          <CardDescription className="text-base">
            {isFromOnboarding
              ? "Save your personalized AI learning path and start your journey"
              : "Join thousands learning AI for social good"
            }
          </CardDescription>
          {isFromOnboarding && (
            <div className="mt-3 p-3 bg-cyan-50 rounded-lg shadow-md border border-cyan-100 animate-in fade-in slide-in-from-top-2 duration-300">
              <p className="text-sm text-center font-medium text-cyan-800">
                We've saved your progress. <br />
                Create an account to keep your personalized path.
              </p>
            </div>
          )}
        </CardHeader>

        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signup">Create Account</TabsTrigger>
              <TabsTrigger value="signin">Sign In</TabsTrigger>
            </TabsList>

            <TabsContent value="signup" className="mt-6">
              {isPrefilledFromLicense && (
                <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-green-800 text-center">
                    Your info has been filled from your purchase. Just create a password to get started!
                  </p>
                </div>
              )}
              <form onSubmit={(e) => handleSubmit(e, false)} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-first-name">First Name</Label>
                  <Input
                    id="signup-first-name"
                    name="given-name"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    placeholder="Your first name"
                    autoComplete="given-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-email">
                    Email
                    {isPrefilledFromLicense && (
                      <span className="ml-2 text-xs text-gray-500">(from your purchase)</span>
                    )}
                  </Label>
                  <Input
                    id="signup-email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="your@email.com"
                    autoComplete="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    inputMode="email"
                    spellCheck={false}
                    readOnly={isPrefilledFromLicense}
                    className={isPrefilledFromLicense ? 'bg-gray-50 cursor-not-allowed' : ''}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password</Label>
                  <Input
                    id="signup-password"
                    name="new-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    minLength={6}
                    autoComplete="new-password"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-cyan-600 hover:bg-cyan-700 text-lg font-semibold py-3"
                  disabled={loading}
                >
                  {loading ? 'Creating Account...' : 'Create Account & Start Learning'}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="signin" className="mt-6">
              <form onSubmit={(e) => handleSubmit(e, true)} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signin-email">Email</Label>
                  <Input
                    id="signin-email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="your@email.com"
                    autoComplete="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    inputMode="email"
                    spellCheck={false}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signin-password">Password</Label>
                  <Input
                    id="signin-password"
                    name="current-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    minLength={6}
                    autoComplete="current-password"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-cyan-600 hover:bg-cyan-700 text-lg font-semibold py-3"
                  disabled={loading}
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth;
